import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Smartphone, Share, Plus, MoreVertical, Download, CheckCircle2 } from "lucide-react";

const TITLE = "Lägg till Alkoholprovet på hemskärmen | Alkoholprovet";
const DESCRIPTION = "Så installerar du Alkoholprovet som en app på iPhone, Android och dator – öva inför kunskapsprovet för serveringstillstånd direkt från hemskärmen.";

export const Route = createFileRoute("/installera")({
  component: InstallPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://alkoholprovet.se/installera" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/installera" }],
  }),
});

export default function _() {}

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

function InstallPage() {
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BIPEvent);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  return (
    <div className="container-page py-14 sm:py-20">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs">
          <Smartphone className="h-3.5 w-3.5 text-accent" /> Som en app – utan App Store
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold">
          Lägg till Alkoholprovet på hemskärmen
        </h1>
        <p className="mt-3 text-muted-foreground">
          Installera Alkoholprovet som en app i mobilen eller på datorn. Du får en egen ikon på hemskärmen och öppnar den direkt – utan att gå via webbläsaren.
        </p>
      </div>

      {installed && (
        <div className="mt-8 max-w-2xl mx-auto rounded-xl border border-success/30 bg-success/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
          <p className="text-sm">Alkoholprovet är redan installerat på den här enheten.</p>
        </div>
      )}

      {deferred && !installed && (
        <div className="mt-8 max-w-2xl mx-auto rounded-2xl border border-primary bg-card p-6 shadow-elevated text-center">
          <p className="text-sm text-muted-foreground">Din webbläsare stödjer installation med ett klick:</p>
          <button
            onClick={handleInstall}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Download className="h-4 w-4" /> Installera Alkoholprovet
          </button>
        </div>
      )}

      <div className="mt-10 grid gap-5 md:grid-cols-3 max-w-5xl mx-auto">
        <Card title="iPhone & iPad (Safari)">
          <Step n={1}>
            Öppna <strong>alkoholprovet.se</strong> i <strong>Safari</strong> (fungerar inte i Chrome eller andra appar på iOS).
          </Step>
          <Step n={2}>
            Tryck på <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5"><Share className="h-3.5 w-3.5" />Dela</span> i menyraden.
          </Step>
          <Step n={3}>
            Bläddra ner och välj <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5"><Plus className="h-3.5 w-3.5" />Lägg till på hemskärmen</span>.
          </Step>
          <Step n={4}>
            Tryck <strong>Lägg till</strong> uppe till höger. Ikonen visas nu bland dina appar.
          </Step>
        </Card>

        <Card title="Android (Chrome)">
          <Step n={1}>
            Öppna <strong>alkoholprovet.se</strong> i <strong>Chrome</strong>.
          </Step>
          <Step n={2}>
            Tryck på <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5"><MoreVertical className="h-3.5 w-3.5" />Meny</span> uppe till höger.
          </Step>
          <Step n={3}>
            Välj <strong>Lägg till på startskärmen</strong> eller <strong>Installera app</strong>.
          </Step>
          <Step n={4}>
            Bekräfta med <strong>Installera</strong>. Appen läggs till bland dina andra appar.
          </Step>
        </Card>

        <Card title="Dator (Chrome / Edge)">
          <Step n={1}>
            Öppna <strong>alkoholprovet.se</strong> i Chrome eller Edge.
          </Step>
          <Step n={2}>
            Klicka på <Download className="inline h-3.5 w-3.5" /> -ikonen längst till höger i adressfältet.
          </Step>
          <Step n={3}>
            Välj <strong>Installera</strong> i dialogrutan.
          </Step>
          <Step n={4}>
            Alkoholprovet öppnas nu i ett eget fönster och finns i din appmeny.
          </Step>
        </Card>
      </div>

      <div className="mt-10 max-w-2xl mx-auto rounded-2xl border border-border bg-card p-6 shadow-card">
        <h2 className="font-display text-lg font-semibold">Varför installera?</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-success shrink-0" /> Snabb åtkomst – ett tryck från hemskärmen.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-success shrink-0" /> Helskärmsläge utan webbläsarens menyrader.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-success shrink-0" /> Du förblir inloggad mellan övningspass.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-success shrink-0" /> Inget App Store-konto behövs – fungerar direkt.</li>
        </ul>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <ol className="mt-4 space-y-3 text-sm">{children}</ol>
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">{n}</span>
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
